import type { ComponentType } from "react";
import { createBrowserRouter, type RouteObject } from "react-router";
import type { Permission } from "./api/types";
import { AppLayout } from "./components/AppLayout";
import { NotFoundPage, RouteErrorPage } from "./components/ErrorPages";
import { RequireAuth, RequirePermission } from "./components/RequireAuth";
import { ActivityPage } from "./features/activity/ActivityPage";
import { LoginPage } from "./features/auth/LoginPage";
import { SetPasswordPage } from "./features/auth/SetPasswordPage";
import { TasksPage } from "./features/tasks/TasksPage";

/**
 * Stránka načítaná až při otevření (NF-PER-02: rychlé první načtení na mobilu). Hlavní balíček obsahuje jen
 * přihlášení, Činnost a Úkoly; správa, katalog, kontrola i sklady jsou v samostatných souborech.
 */
function page(path: string, load: () => Promise<ComponentType>, permission?: Permission): RouteObject {
  return {
    path,
    lazy: async () => {
      const Page = await load();
      return {
        Component: permission
          ? () => (
              <RequirePermission permission={permission}>
                <Page />
              </RequirePermission>
            )
          : Page,
      };
    },
  };
}

// Adresy jsou česky (zobrazují se uživateli v prohlížeči a ve sdílených odkazech).
export const router = createBrowserRouter([
  { path: "/prihlaseni", element: <LoginPage />, errorElement: <RouteErrorPage /> },
  { path: "/nastavit-heslo", element: <SetPasswordPage />, errorElement: <RouteErrorPage /> },
  {
    element: (
      <RequireAuth>
        <AppLayout />
      </RequireAuth>
    ),
    errorElement: <RouteErrorPage />,
    children: [
      { index: true, element: <ActivityPage /> },
      {
        path: "ukoly",
        element: (
          <RequirePermission permission="TasksWork">
            <TasksPage />
          </RequirePermission>
        ),
      },
      page("start", async () => (await import("./features/activity/StartPage")).StartPage),
      page("souhrn", async () => (await import("./features/activity/DaySummary")).DaySummaryPage),
      page("nastaveni", async () => (await import("./features/settings/SettingsPage")).SettingsPage),
      page("sklady", async () => (await import("./features/stock/StockPage")).StockPage, "StockView"),
      page("kontrola", async () => (await import("./features/checks/CheckOverviewPage")).CheckOverviewPage, "CheckPerform"),
      page("kontrola/sklad/:warehouseId", async () => (await import("./features/checks/CheckRunPage")).CheckRunPage, "CheckPerform"),
      page("kontrola/historie", async () => (await import("./features/checks/CheckHistoryPage")).CheckHistoryPage, "CheckPerform"),
      page("kontrola/historie/:runId", async () => (await import("./features/checks/CheckHistoryPage")).CheckHistoryDetailPage, "CheckPerform"),
      page("ukoly/kontrola", async () => (await import("./features/tasks/ReviewPage")).ReviewPage, "TasksReview"),
      page("sprava/sablony", async () => (await import("./features/tasks/TemplatesPage")).TemplatesPage, "TaskTemplatesManage"),
      page("sprava/uzivatele", async () => (await import("./features/admin/UsersPage")).UsersPage, "UsersManage"),
      page("sprava/oddeleni", async () => (await import("./features/admin/DepartmentsPage")).DepartmentsPage, "CatalogManage"),
      page("sprava/provozovna", async () => (await import("./features/admin/LocationPage")).LocationPage, "LocationSettingsManage"),
      page("sprava/sklady", async () => (await import("./features/catalog/WarehousesPage")).WarehousesPage, "CatalogManage"),
      page("sprava/sklady/:id", async () => (await import("./features/catalog/WarehouseOrderPage")).WarehouseOrderPage, "CatalogManage"),
      page("sprava/kategorie", async () => (await import("./features/catalog/CategoriesPage")).CategoriesPage, "CatalogManage"),
      page("sprava/polozky", async () => (await import("./features/catalog/ItemsPage")).ItemsPage, "CatalogManage"),
      page("sprava/polozky/:id", async () => (await import("./features/catalog/ItemDetailPage")).ItemDetailPage, "CatalogManage"),
      page("sprava/import", async () => (await import("./features/catalog/ImportPage")).ImportPage, "CatalogManage"),
      { path: "*", element: <NotFoundPage /> },
    ],
  },
]);
